import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/pages/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  Snowflake,
  Unlock,
  KeyRound,
  Trash2,
  Plus,
  Minus,
  Save,
  Loader2,
} from "lucide-react";

export default function AdminUserDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adj, setAdj] = useState({ field: "balance_live", amount: "", reason: "" });
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, id]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  if (loading || !data) return <AdminShell><div className="p-10 text-center text-[#4F4D45]">Loading…</div></AdminShell>;

  const patch = async (changes) => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${id}`, changes);
      toast.success("Updated.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const applyAdjust = async (sign) => {
    const amt = Number(adj.amount);
    if (!amt || !adj.reason) { toast.error("Enter an amount and a reason."); return; }
    try {
      const { data: res } = await api.post(`/admin/users/${id}/adjust_balance`, {
        field: adj.field, amount: sign * Math.abs(amt), reason: adj.reason,
      });
      toast.success(`Adjusted to $${res.new_balance.toFixed(2)}.`);
      setAdj({ ...adj, amount: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disable2fa = async () => {
    try { await api.post(`/admin/users/${id}/disable_2fa`); toast.success("2FA disabled."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReset = async () => {
    if (resetPw.length < 8) { toast.error("Min 8 chars."); return; }
    try { await api.post(`/admin/users/${id}/reset_password`, { new_password: resetPw }); toast.success("Password reset. Share securely with the user."); setResetPw(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${data.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted."); navigate("/admin/users"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addr = data.address;
  return (
    <AdminShell>
      <Link to="/admin/users" className="text-sm text-[#4F4D45] hover:text-[#1B3526] inline-flex items-center gap-1.5 mb-4" data-testid="admin-userdetail-back">
        <ArrowLeft className="w-4 h-4" /> Back to users
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">User</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1" data-testid="admin-userdetail-name">{data.name}</h1>
          <div className="text-[#4F4D45] mt-1">{data.email} · <span className="font-mono text-xs">{data.wallet_id}</span></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => patch({ account_status: "active" })} disabled={saving || data.account_status === "active"} variant="outline" className="border-[#D2CDBE] text-[#264C39]" data-testid="admin-userdetail-unlock">
            <Unlock className="w-4 h-4 mr-2" /> Unlock
          </Button>
          <Button onClick={() => patch({ account_status: "frozen" })} disabled={saving || data.account_status === "frozen"} variant="outline" className="border-[#D2CDBE] text-[#1B3526]" data-testid="admin-userdetail-freeze">
            <Snowflake className="w-4 h-4 mr-2" /> Freeze
          </Button>
          <Button onClick={() => patch({ account_status: "locked" })} disabled={saving || data.account_status === "locked"} variant="outline" className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white" data-testid="admin-userdetail-lock">
            <Lock className="w-4 h-4 mr-2" /> Lock
          </Button>
          <Button onClick={disable2fa} variant="outline" className="border-[#D2CDBE]" data-testid="admin-userdetail-2fa">
            <KeyRound className="w-4 h-4 mr-2" /> Disable 2FA
          </Button>
          {data.role !== "admin" && (
            <Button onClick={remove} variant="outline" className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white" data-testid="admin-userdetail-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Profile */}
        <div className="fv-card p-6">
          <h2 className="font-display text-xl font-medium">Profile</h2>
          <ul className="mt-4 text-sm space-y-2">
            <Row k="Account type" v={data.account_type} />
            <Row k="Banking mode" v={data.banking_mode} />
            <Row k="Role" v={data.role} />
            <Row k="Business name" v={data.business_name || "—"} />
            <Row k="Phone" v={data.phone || "—"} />
            <Row k="Date of birth" v={data.date_of_birth || "—"} />
            <Row k="Address" v={[addr?.line1, addr?.city, addr?.state, addr?.country].filter(Boolean).join(", ") || "—"} />
            <Row k="KYC status" v={data.kyc_status || "—"} />
            <Row k="Suspended" v={data.is_suspended ? "Yes" : "No"} />
            <Row k="2FA" v={data.two_factor_enabled ? "Enabled" : "Disabled"} />
          </ul>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <select defaultValue={data.banking_mode || "demo"} onChange={(e) => patch({ banking_mode: e.target.value })} className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm" data-testid="admin-userdetail-mode">
              <option value="demo">demo</option>
              <option value="live">live</option>
            </select>
            <select defaultValue={data.role || "user"} onChange={(e) => patch({ role: e.target.value })} className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm" data-testid="admin-userdetail-role">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
        </div>

        {/* Balances */}
        <div className="fv-card p-6">
          <h2 className="font-display text-xl font-medium">Balances</h2>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Pill label="Live" value={`$${data.balance_live.toFixed(2)}`} accent="text-[#1B3526]" />
            <Pill label="Demo" value={`$${data.balance_demo.toFixed(2)}`} accent="text-[#4F4D45]" />
          </div>
          <div className="mt-5">
            <Label className="text-eyebrow text-[#4F4D45]">Adjust balance</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <select value={adj.field} onChange={(e) => setAdj({ ...adj, field: e.target.value })} className="px-2 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm" data-testid="admin-adjust-field">
                <option value="balance_live">Live</option>
                <option value="balance_demo">Demo</option>
              </select>
              <Input value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Amount" data-testid="admin-adjust-amount" />
            </div>
            <Textarea value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Reason (audit log)" className="mt-2" data-testid="admin-adjust-reason" />
            <div className="mt-3 flex gap-2">
              <Button onClick={() => applyAdjust(1)} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="admin-adjust-credit"><Plus className="w-4 h-4 mr-1.5" /> Credit</Button>
              <Button onClick={() => applyAdjust(-1)} variant="outline" className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white" data-testid="admin-adjust-debit"><Minus className="w-4 h-4 mr-1.5" /> Debit</Button>
            </div>
          </div>
        </div>

        {/* Password reset */}
        <div className="fv-card p-6">
          <h2 className="font-display text-xl font-medium">Force password reset</h2>
          <p className="text-sm text-[#4F4D45] mt-2">Use only on verified support requests. Share the new password via a secure channel.</p>
          <div className="mt-4">
            <Label className="text-eyebrow text-[#4F4D45]">New password</Label>
            <PasswordInput value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="mt-1" testId="admin-reset-input" placeholder="At least 8 characters" />
          </div>
          <Button onClick={submitReset} className="mt-4 bg-[#1B3526] hover:bg-[#0F2517]" data-testid="admin-reset-submit">
            <Save className="w-4 h-4 mr-2" /> Set new password
          </Button>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="mt-6 fv-card overflow-hidden">
        <div className="px-6 py-4 border-b border-[#D2CDBE]">
          <h2 className="font-display text-xl font-medium">Recent transactions</h2>
        </div>
        {data.transactions.length === 0 ? (
          <div className="p-10 text-center text-[#4F4D45]">No activity.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-userdetail-tx-table">
            <thead className="bg-[#F0ECE0] text-left text-xs uppercase tracking-wider text-[#4F4D45]">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Description</th>
                <th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D2CDBE]">
              {data.transactions.map((t) => (
                <tr key={t.id} className="hover:bg-[#F0ECE0]/60">
                  <td className="px-5 py-2 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2 capitalize">{(t.type || "").replace("_", " ")}</td>
                  <td className="px-5 py-2">{t.description}</td>
                  <td className="px-5 py-2 capitalize">{t.mode}</td>
                  <td className="px-5 py-2 capitalize">{t.status}</td>
                  <td className="px-5 py-2 text-right font-mono">${(t.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}

function Row({ k, v }) { return <li className="flex items-start justify-between gap-4"><span className="text-[#4F4D45]">{k}</span><span className="text-right capitalize">{v}</span></li>; }
function Pill({ label, value, accent }) { return <div className="rounded-xl bg-[#F0ECE0] border border-[#D2CDBE] p-4"><div className="text-eyebrow text-[#4F4D45]">{label}</div><div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div></div>; }
